import { z } from "zod"

import { choiceSchema } from "./choice"

export const propertyKeySchema = z.object({
  /** The unique name for this property. */
  keyName: z.string().nullable(),
  /** Sub-property name for this property. */
  subKeyName: z.string().nullable().optional(),
  /** A string that can be used to display to users. */
  displayName: z.string().nullable().optional(),
  /** Second level of the categorization hierarchy, family
   * groups keyNames together into logical areas, such as "billing",
   * "generation", "load", "peakDemand", and "ratchetDemand". */
  family: z.string().nullable().optional(),
  /** The top level of the categorization hierarchy, keyspace
   * defines major groups of keyNames, such as "customer",
   * "electricity", "market", “tariff”, and "solarPV". */
  keyspace: z.string().nullable().optional(),
  /** A user-friendly detailed description of the purpose of this property. */
  description: z.string().nullable().optional(),
  /** The data type of this property. Possible values are
   * STRING, CHOICE, BOOLEAN, DATE, DECIMAL, INTEGER,
   * FORMULA, LOOKUP (see Lookup API), or DEMAND. */
  dataType: z.string().nullable().optional(),
  /** The unit that applies to this property. e.g. "kWh", "Inches", "HP" */
  quantityUnit: z.string().nullable().optional(),
  /** For properties that specify a formula, this is the actual formula used. */
  formulaDetail: z.string().nullable().optional(),
  /** For properties of type DEMAND, this specifies the
   * size of the interval in minutes. */
  lookbackIntervalQuantity: z.number().nullable().optional(),
  /** For properties of type DEMAND, this specifies the
   * number of lookback periods. */
  lookbackQuantity: z.number().nullable().optional(),
  /** For properties of type DEMAND, this specifies the
   * duration of a single lookback period. */
  lookbackPeriod: z.string().nullable().optional(),
  /** For properties of type DEMAND, this specifies the
   * applicable timeOfUseId. */
  lookbackTimeOfUseId: z.number().nullable().optional(),
  /** For properties of type DEMAND, this specifies the applicable seasonId. */
  lookbackSeasonId: z.number().nullable().optional(),
  /** The ID of the entity this property key belongs to.
   * For example, for an LSE-specific property, this will
   * be the LSE ID. For general properties, this will be null. */
  entityId: z.number().nullable().optional(),
  /** The type of entity this property key belongs to.
   * For example, for an LSE-specific property, this will
   * be "LSE". For general properties, this will be null. */
  entityType: z.string().nullable().optional(),
  /** For properties of type CHOICE or LOOKUP,
   * this contains the possible choices to choose from. */
  choices: z.array(choiceSchema).nullable().optional(),
})

export type PropertyKey = z.infer<typeof propertyKeySchema>
